import { CategoryType, PoolGamesType, PoolRankingType } from "../types";
import { groupBy } from "../utils";
import {
  ActionFieldsType,
  AirtableRecordType,
  AirtableTableType,
  AirtableViewType,
  ChallengeFieldsType,
  FeatureFlagsFieldsType,
  FieldsFieldsType,
  GameFieldsType,
  PersonFieldsType,
  RegisterFieldsType,
  SponsorsFieldsType,
  TeamsFieldsType,
} from "./types";

const config = {
  base: process.env.REACT_APP_AIRTABLE_BASE,
  apiKey: process.env.REACT_APP_AIRTABLE_API_KEY,
  maxRecords: process.env.REACT_APP_AIRTABLE_MAX_RECORDS, // 100 is the max
};

const getRequest = (
  table: AirtableTableType,
  view: AirtableViewType,
  filterByFormula?: string,
  otherParams?: Record<string, string>
) => {
  const params = new URLSearchParams({
    maxRecords: config.maxRecords ?? "",
    view,
    ...(filterByFormula && { filterByFormula }),
    ...otherParams,
  });

  return new Request(
    `https://api.airtable.com/v0/${config.base}/${table}?${params}`,
    {
      method: "GET",
      headers: new Headers({
        Authorization: `Bearer ${config.apiKey}`,
      }),
    }
  );
};

const getRequestById = (table: AirtableTableType, id: string) => {
  return new Request(
    `https://api.airtable.com/v0/${config.base}/${table}/${id}`,
    {
      method: "GET",
      headers: new Headers({
        Authorization: `Bearer ${config.apiKey}`,
      }),
    }
  );
};

const updateRecord = (
  table: AirtableTableType,
  recordId: string,
  data: Record<string, any>
) => {
  return new Request(
    `https://api.airtable.com/v0/${config.base}/${table}/${recordId}`,
    {
      method: "PATCH",
      headers: new Headers({
        Authorization: `Bearer ${config.apiKey}`,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        fields: data,
      }),
    }
  );
};

const fetchAirtableRecords = async <T>(
  table: AirtableTableType,
  view: AirtableViewType,
  filterByFormula?: string,
  otherParams?: Record<string, string>
) => {
  try {
    const resp = (await fetch(
      getRequest(table, view, filterByFormula, otherParams)
    ).catch((err) => {
      console.log(err);
    })) as Response;
    if (resp.status >= 200 && resp.status < 300) {
      const json = await resp.json();
      const { records } = json;
      return records as T[];
    }
  } catch (error) {
    console.log(error);
  }
};

const fetchAirtableRecordById = async <T>(
  table: AirtableTableType,
  id: string
) => {
  try {
    const resp = (await fetch(getRequestById(table, id)).catch((err) => {
      console.log(err);
    })) as Response;
    if (resp.status >= 200 && resp.status < 300) {
      const json = await resp.json();
      return json as T;
    }
  } catch (error) {
    console.log(error);
  }
};

const updateAirtableRecord = async (
  table: AirtableTableType,
  recordId: string,
  data: Record<string, any>
) => {
  try {
    const resp = await fetch(updateRecord(table, recordId, data));
    console.log(resp);
    if (resp.status >= 200 && resp.status < 300) {
      const json = await resp.json();
      const { records } = json;
      return records as AirtableRecordType<RegisterFieldsType>;
    }
  } catch (error) {
    console.log(error);
  }
};

export const fetchRanking = async (params: {
  selectedCategory: CategoryType | undefined;
  dayPart: "am" | "pm";
}): Promise<PoolRankingType[] | undefined> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<TeamsFieldsType>
    >(
      params.dayPart === "am" ? "ranking_am" : "ranking_pm",
      "list",
      params.selectedCategory
        ? `{category}="${params.selectedCategory}"`
        : undefined
    );
    if (data) {
      const pools = groupBy(data, (team) => team.fields.pool);
      const poolsArray: PoolRankingType[] = Object.keys(pools).map((pool) => ({
        id: pool,
        name: pool,
        ranking: pools[pool].map((team) => team.fields),
      }));
      return poolsArray;
    }
  } catch (error) {
    console.log(error);
  }
};

export const fetchGames = async (params: {
  selectedCategory: CategoryType | undefined;
  dayPart: "am" | "pm";
}): Promise<PoolGamesType[] | undefined> => {
  try {
    const data = await fetchAirtableRecords<AirtableRecordType<GameFieldsType>>(
      params.dayPart === "am" ? "games" : "games_pm",
      "list",
      params.selectedCategory
        ? `{category}="${params.selectedCategory}"`
        : undefined
    );
    if (data) {
      const pools = groupBy(data, (game) => game.fields.pool);
      const poolsArray: PoolGamesType[] = Object.keys(pools).map((pool) => ({
        id: pool,
        name: pool,
        games: pools[pool]
          .map((game) => game.fields)
          .sort((a, b) => {
            // sort by date
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA.getTime() - dateB.getTime();
          }),
      }));
      return poolsArray;
    }
  } catch (error) {
    console.log(error);
  }
};

export const fetchSponsors = async (): Promise<
  SponsorsFieldsType[] | undefined
> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<SponsorsFieldsType>
    >("sponsors", "list");
    if (data) {
      return data.map((sponsor) => sponsor.fields);
    }
  } catch (error) {
    console.log(error);
  }
};

export const fetchChallengeRanking = async (): Promise<
  ChallengeFieldsType[] | undefined
> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<ChallengeFieldsType>
    >("challenge", "list");
    if (data) {
      return data.map((team) => team.fields);
    }
  } catch (error) {
    console.log(error);
  }
};

export const fetchFieldGames = async ({
  field,
  dayPart,
}: {
  field?: string;
  dayPart: "am" | "pm";
}): Promise<GameFieldsType[] | undefined> => {
  try {
    const data = await fetchAirtableRecords<AirtableRecordType<GameFieldsType>>(
      dayPart === "am" ? "games" : "games_pm",
      "list",
      `{field_name}="${field}"`
    );
    if (data) {
      return data.map((games) => games.fields);
    }
  } catch (error) {
    console.log(error);
  }
};

export const fetchFields = async (): Promise<
  FieldsFieldsType[] | undefined
> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<FieldsFieldsType>
    >("fields", "list");
    if (data) {
      return data.map((field) => field.fields);
    }
  } catch (error) {
    console.log(error);
  }
};

export const fetchFeatureFlags = async () => {
  try {
    const records = await fetchAirtableRecords<
      AirtableRecordType<FeatureFlagsFieldsType>
    >("feature_flags", "list");
    const recordFields = records?.map((record) => record.fields);
    return recordFields?.reduce((acc, flag) => {
      return {
        ...acc,
        [flag.id]: flag?.is_enabled ?? false,
      };
    }, {});
  } catch (error) {
    console.log(error);
  }
};

export const registerTeam = async (
  data: Record<string, any>
): Promise<AirtableRecordType<RegisterFieldsType> | undefined> => {
  try {
    const resp = await fetch(getRequest("register", "list"), {
      method: "POST",
      headers: new Headers({
        Authorization: `Bearer ${config.apiKey}`,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        fields: data,
      }),
    });
    return await resp.json();
  } catch (error) {
    console.log(error);
  }
};

export const fetchRegister = async (
  teamId: string | null | undefined
): Promise<AirtableRecordType<RegisterFieldsType> | undefined> => {
  if (!teamId) {
    return;
  }
  try {
    const response = await fetchAirtableRecordById<
      AirtableRecordType<RegisterFieldsType>
    >("register", teamId);
    return response;
  } catch (error) {
    console.log(error);
  }
};

export const updateRegister = async ({
  recordId,
  ...data
}: Record<string, any>): Promise<
  AirtableRecordType<RegisterFieldsType> | undefined
> => {
  try {
    const response = await updateAirtableRecord("register", recordId, data);
    return response;
  } catch (error) {
    console.log(error);
  }
};

export const fetchTeamPersons = async (
  teamId: string | null | undefined
): Promise<AirtableRecordType<PersonFieldsType>[] | undefined> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<PersonFieldsType>
    >("persons", "list", `FIND("${teamId}", {team_record_id} & '')`);
    return data;
  } catch (error) {
    console.log(error);
  }
};

export const fetchPlayersByStat = async (
  stat: "tries" | "conversions" | "penalties" | "drops" | "points"
): Promise<AirtableRecordType<PersonFieldsType>[] | undefined> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<PersonFieldsType>
    >("persons", "list", undefined, {
      maxRecords: "10",
      "sort[0][field]": stat,
      "sort[0][direction]": "desc",
    });
    return data;
  } catch (error) {
    console.log(error);
  }
};

export const fetchTeamsByStat = async (
  stat: "points_scored" | "points_taken"
): Promise<AirtableRecordType<TeamsFieldsType>[] | undefined> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<TeamsFieldsType>
    >("teams", "list", undefined, {
      maxRecords: "10",
      "sort[0][field]": stat,
      "sort[0][direction]": stat === "points_scored" ? "desc" : "asc",
    });
    return data;
  } catch (error) {
    console.log(error);
  }
};

export const createPerson = async (
  data: Record<string, any>
): Promise<AirtableRecordType<PersonFieldsType> | undefined> => {
  try {
    const resp = await fetch(getRequest("persons", "list"), {
      method: "POST",
      headers: new Headers({
        Authorization: `Bearer ${config.apiKey}`,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        fields: data,
      }),
    });
    return await resp.json();
  } catch (error) {
    console.log(error);
  }
};

export const updatePerson = async (
  recordId: string,
  data: Record<string, any>
): Promise<AirtableRecordType<PersonFieldsType> | undefined> => {
  try {
    const resp = await fetch(updateRecord("persons", recordId, data));
    return await resp.json();
  } catch (error) {
    console.log(error);
  }
};

export const deletePerson = async (recordId: string): Promise<void> => {
  try {
    await fetch(getRequestById("persons", recordId), {
      method: "DELETE",
      headers: new Headers({
        Authorization: `Bearer ${config.apiKey}`,
      }),
    });
  } catch (error) {
    console.log(error);
  }
};

const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

export const uploadAttachment = async (
  recordId: string,
  fieldName: string,
  file: File
): Promise<string | undefined> => {
  const reader = new FileReader();
  reader.readAsArrayBuffer(file);

  try {
    const resp = await fetch(
      `https://content.airtable.com/v0/${config.base}/${recordId}/${fieldName}/uploadAttachment`,
      {
        method: "POST",
        headers: new Headers({
          Authorization: `Bearer ${config.apiKey}`,
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          contentType: file.type,
          // Base64 encode the file
          file: await toBase64(file),
          filename: file.name,
        }),
      }
    );
    const json = await resp.json();
    return json.url;
  } catch (error) {
    console.log(error);
  }
};

export const fetchGameActions = async (
  gameId: string | undefined
): Promise<AirtableRecordType<ActionFieldsType>[] | undefined> => {
  if (!gameId) {
    return;
  }
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<ActionFieldsType>
    >(
      "actions",
      "list",
      `OR({games_record_id}="${gameId}", {games_pm_record_id}="${gameId}")`
    );
    return (
      data
        ?.filter(
          (action) =>
            action.fields.games?.includes(gameId) ||
            action.fields.games_pm?.includes(gameId)
        )
        .reverse() ?? []
    );
  } catch (error) {
    console.log(error);
  }
};

export const fetchTeams = async (): Promise<
  AirtableRecordType<TeamsFieldsType>[] | undefined
> => {
  try {
    const data = await fetchAirtableRecords<
      AirtableRecordType<TeamsFieldsType>
    >("teams", "list");
    return data;
  } catch (error) {
    console.log(error);
  }
};

export const fetchTeamById = async (
  teamId: string
): Promise<AirtableRecordType<TeamsFieldsType> | undefined> => {
  try {
    const data = await fetchAirtableRecordById<
      AirtableRecordType<TeamsFieldsType>
    >("teams", teamId);
    return data;
  } catch (error) {
    console.log(error);
  }
};
